import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { mediaUrl } from "../lib/media";

const SettingsContext = createContext(null);

const CACHE_KEY = "hs_settings_v1";

// Dipakai pada render pertama supaya logo & tema tidak berkedip sambil
// /api/settings masih jalan di latar belakang.
const bacaCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const FONT_STACK = {
  "Hanken Grotesk": "'Hanken Grotesk', ui-sans-serif, -apple-system, 'Segoe UI', sans-serif",
  "Inter": "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  "Plus Jakarta Sans": "'Plus Jakarta Sans', sans-serif",
  "Manrope": "'Manrope', sans-serif",
};

// PR46 tambahan D (Haryanto 29/10/2026; review Codex): tag ikon di <head> disamakan dengan pengaturan memakai aturan yang SAMA dengan HTML
// server (server/seo.js ikonHead): favicon PNG → sizes 32x32, SVG/ICO → any, kosong → /favicon.ico; varian 192/512 dihapus bila kosong;
// apple-touch-icon tepat satu tag (icon_180_url, atau /apple-touch-icon.png bila kosong). Tag yang sudah ada diperbarui, tidak ditambah ganda.
export function sinkronIkon(settings, doc = document) {
  const buat = (rel) => { const el = doc.createElement("link"); el.rel = rel; doc.head.appendChild(el); return el; };
  const fav = String(settings.favicon_url || "").trim();
  const kecil = doc.querySelector("link[rel='icon'][sizes='32x32'], link[rel='icon'][sizes='any']")
    || doc.querySelector("link[rel~='icon']:not([sizes='192x192']):not([sizes='512x512'])") || buat("icon");
  kecil.href = fav ? mediaUrl(fav) : "/favicon.ico";
  kecil.setAttribute("sizes", !fav || /\.(svg|ico)$/i.test(fav) ? "any" : "32x32");
  for (const [sizes, path] of [["192x192", settings.icon_192_url], ["512x512", settings.icon_512_url]]) {
    const ada = [...doc.querySelectorAll(`link[rel='icon'][sizes='${sizes}']`)];
    if (!path) { ada.forEach((el) => el.remove()); continue; }
    const el = ada.shift() || buat("icon");
    ada.forEach((x) => x.remove());
    el.type = "image/png"; el.setAttribute("sizes", sizes); el.href = mediaUrl(path);
  }
  const apel = [...doc.querySelectorAll("link[rel='apple-touch-icon']")];
  const a = apel.shift() || buat("apple-touch-icon");
  apel.forEach((x) => x.remove());
  if (settings.icon_180_url) { a.setAttribute("sizes", "180x180"); a.href = mediaUrl(settings.icon_180_url); }
  else { a.removeAttribute("sizes"); a.href = "/apple-touch-icon.png"; }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(bacaCache);
  const { pathname } = useLocation();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/settings");
      setSettings(data);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* kuota penuh */ }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!settings) return;
    // Meta title/description dari Pengaturan HANYA untuk beranda; halaman lain memakai metanya sendiri.
    if (pathname === "/") {
      document.title = settings.meta_title || settings.nama_toko || "HoonStore";
      if (settings.meta_description) {
        let m = document.querySelector("meta[name='description']");
        if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
        m.content = settings.meta_description;
      }
    }
    sinkronIkon(settings);
    const font = FONT_STACK[settings.font_utama] || FONT_STACK["Hanken Grotesk"];
    document.documentElement.style.setProperty("--hs-font", font);
  }, [settings, pathname]);

  return (
    <SettingsContext.Provider value={{ settings, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext) || { settings: null, refresh: () => {} };
